import { useState, useEffect, useRef } from 'react';
import { Activity, Zap, Users, ArrowDownCircle, ArrowUpCircle, Gamepad2, LogIn } from 'lucide-react';
import { fmtUSD } from '../../lib/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ACTION_ICONS = {
  login: { icon: LogIn, color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  deposit: { icon: ArrowDownCircle, color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  withdraw: { icon: ArrowUpCircle, color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  game_load: { icon: Gamepad2, color: '#D4AF37', bg: 'rgba(212,175,55,0.12)' },
  game_redeem: { icon: Gamepad2, color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
};

export default function LiveActivityPage() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [stats, setStats] = useState({ total: 0, logins: 0, deposits: 0, game_actions: 0 });
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    connectWS();
    return () => {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const connectWS = () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    const wsBase = (BACKEND_URL || '').replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsBase}/api/ws/live-activity?token=${token}`);

    ws.onopen = () => {
      setConnected(true);
      ws._ping = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'live_event' && data.event) {
          setEvents(prev => [data.event, ...prev].slice(0, 100));
          setStats(prev => ({
            total: prev.total + 1,
            logins: prev.logins + (data.event.action === 'login' ? 1 : 0),
            deposits: prev.deposits + (data.event.action === 'deposit' ? 1 : 0),
            game_actions: prev.game_actions + (['game_load', 'game_redeem'].includes(data.event.action) ? 1 : 0),
          }));
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      clearInterval(ws._ping);
      reconnectRef.current = setTimeout(connectWS, 3000);
    };
    ws.onerror = () => ws.close();
    wsRef.current = ws;
  };

  return (
    <div className="space-y-5" data-testid="live-activity-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Zap className="w-5 h-5 text-[#D4AF37]" /> Live Activity</h2>
          <span className={`text-[9px] px-2.5 py-1 rounded-full font-semibold ${connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {connected ? 'Connected' : 'Connecting...'}
          </span>
        </div>
        <p className="text-xs text-gray-500">Real-time player events</p>
      </div>

      {/* Live Counters */}
      <div className="grid grid-cols-4 gap-3">
        <LiveCounter label="Total Events" value={stats.total} color="#D4AF37" icon={Activity} />
        <LiveCounter label="Logins" value={stats.logins} color="#3B82F6" icon={LogIn} />
        <LiveCounter label="Deposits" value={stats.deposits} color="#22c55e" icon={ArrowDownCircle} />
        <LiveCounter label="Game Actions" value={stats.game_actions} color="#8b5cf6" icon={Gamepad2} />
      </div>

      {/* Live Feed */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
        <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
          <div className="relative">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <div className="absolute inset-0 w-2 h-2 rounded-full bg-green-400 animate-ping" />
          </div>
          Live Feed
        </h3>
        {events.length === 0 ? (
          <div className="text-center py-16">
            <Zap className="w-10 h-10 mx-auto mb-3 text-gray-600" />
            <p className="text-gray-500 text-sm">Waiting for live events...</p>
            <p className="text-gray-600 text-xs mt-1">Events will appear as players login, deposit, or play games</p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[480px] overflow-y-auto">
            {events.map((evt, i) => {
              const cfg = ACTION_ICONS[evt.action] || ACTION_ICONS.login;
              const Icon = cfg.icon;
              const time = evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : '';
              return (
                <div key={`${evt.timestamp}-${i}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#0B0E14] transition-all" style={{ animation: i === 0 ? 'fadeInUp 0.3s ease' : undefined }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cfg.bg }}>
                    <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{evt.username || 'Unknown'}</p>
                    <p className="text-[10px] text-gray-500 capitalize">{evt.action?.replace('_', ' ')}{evt.metadata?.amount ? ` - ${fmtUSD(evt.metadata.amount)}` : ''}</p>
                  </div>
                  <span className="text-[10px] text-gray-600 flex-shrink-0">{time}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function LiveCounter({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-[10px] text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
        </div>
      </div>
    </div>
  );
}
